import { ref } from 'vue'
import type { Environment, Metadata } from '@workspaces/types'
import { Poi } from '@workspaces/types'
import { composePoiPinIcon } from './poi-pin-compositor'
import {
  getIdsForBrands,
  getIdsForSubCategories,
  getIdsForTags,
  getIdsForTopCategories,
} from '@/services/pois-tileset.service'
import type { FilterByPoisParamsRequestData } from '@/plan/filter/pois.type'

/**
 * POI logos cache (sc-563940).
 *
 * `plan.poisIcons` is keyed by selection name, but standard-POI tile features
 * only carry encoded classification values (`b`, `sc`, `tc`, `cts`,
 * `location_name`). This singleton resolves tile values (and custom-POI group
 * ids) to the uploaded icon of their selection, mirroring how
 * pois-filter-cache pre-resolves names to ids for `filterPois`.
 */

export interface PoiLogoIcon {
  /**
   * Unique id for deck.gl's IconManager: the logo URL plus the fit it was
   * composed with (both immutable by design).
   */
  id: string
  url: string
  width: number
  height: number
  /** Pin tip anchor, so the marker points at the POI location. */
  anchorY: number
}

/** A selection's uploaded logo URL and the marker fit to compose it with. */
export type PoiLogoSource = Poi.PoiIconUrlEntry

interface PoiLogosCache {
  /** Tileset brand id (`properties.b`) -> icon. */
  brands: Map<number, PoiLogoIcon>
  /** Tag id (matched inside `properties.cts`, format `#1#4#`) -> icon. */
  tags: Map<number, PoiLogoIcon>
  /** `properties.location_name` -> icon. */
  locations: Map<string, PoiLogoIcon>
  /** Tileset top-category id (`properties.tc`) -> icon. */
  topCategories: Map<number, PoiLogoIcon>
  /** Tileset sub-category id (`properties.sc`) -> icon. */
  subCategories: Map<number, PoiLogoIcon>
  /** Custom-POIs group id (`properties.id`) -> icon. */
  customGroups: Map<string, PoiLogoIcon>
  /** Selection name -> icon (poisColors and the legend rows use this key). */
  selections: Map<string, PoiLogoIcon>
}

/** The encoded properties of a POI tileset feature (see PoiTileset). */
export interface PoiTileProperties {
  b?: number
  sc?: number | null
  tc?: number
  cts?: string
  location_name?: string
}

export type PinIconCompositor = typeof composePoiPinIcon

function emptyCache(): PoiLogosCache {
  return {
    brands: new Map(),
    tags: new Map(),
    locations: new Map(),
    topCategories: new Map(),
    subCategories: new Map(),
    customGroups: new Map(),
    selections: new Map(),
  }
}

let cache: PoiLogosCache = emptyCache()

/**
 * Bumped on every cache change. This is a module-level cache, so deck.gl cannot
 * see it change; accessors that read it (getIcon, and the filters that split
 * logos from circles) use this as their `updateTriggers` value so they are
 * re-evaluated when the cache actually changes instead of on every render.
 *
 * A Vue ref so component computeds that read the cache (the legend's rows)
 * re-run when it is rebuilt — the rebuild is async and lands after the store
 * flags that trigger it have already changed.
 */
const generation = ref(0)

export function getPoiLogosCacheGeneration(): number {
  return generation.value
}

async function buildIcon(
  source: PoiLogoSource,
  compositor: PinIconCompositor,
): Promise<PoiLogoIcon | null> {
  try {
    const composed = await compositor(source.url, source.fit)
    return {
      id: `${source.url}#${source.fit}`,
      url: composed.url,
      width: composed.width,
      height: composed.height,
      anchorY: composed.anchorY,
    }
  } catch (error) {
    console.warn(
      `[poi-logos-cache] Skipping unloadable icon ${source.url}`,
      error,
    )
    return null
  }
}

/**
 * Rebuilds the cache from the plan's POI selections and their logo sources
 * (URL + marker fit). Selections without an assigned icon are skipped; logos
 * that fail to load or compose are skipped too (their POIs keep rendering as
 * circles).
 *
 * URLs, not file basenames: a shared plan gets them from the cloud function
 * (the files live under a plan id it never sees), so the cache stays out of
 * the business of knowing where the bucket is.
 */
export async function updatePoiLogosCache(
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
  selections: FilterByPoisParamsRequestData[],
  logoSources: Record<string, PoiLogoSource>,
  compositor: PinIconCompositor = composePoiPinIcon,
): Promise<void> {
  const newCache = emptyCache()

  // Selections are independent (each writes its own keys), so their image
  // compositions and id lookups run concurrently.
  await Promise.all(
    selections.map(async (selection) => {
      const source = logoSources[selection.name]
      if (!source?.url) {
        return
      }
      const icon = await buildIcon(source, compositor)
      if (!icon) {
        return
      }
      newCache.selections.set(selection.name, icon)
      await indexSelection(meta, environment, newCache, selection, icon)
    }),
  )

  cache = newCache
  generation.value += 1
}

async function indexSelection(
  meta: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
  target: PoiLogosCache,
  selection: FilterByPoisParamsRequestData,
  icon: PoiLogoIcon,
): Promise<void> {
  const values = [selection.name]
  switch (selection.type) {
    case Poi.POIsClassification.Brand: {
      const ids = await getIdsForBrands(meta, environment, values)
      ids.forEach((id) => target.brands.set(id, icon))
      break
    }
    case Poi.POIsClassification.Tags: {
      const ids = await getIdsForTags(meta, environment, values)
      ids.forEach((id) => target.tags.set(id, icon))
      break
    }
    case Poi.POIsClassification.Locations: {
      target.locations.set(selection.name, icon)
      break
    }
    case Poi.POIsClassification.Category: {
      const [topIds, subIds] = await Promise.all([
        getIdsForTopCategories(meta, environment, values),
        getIdsForSubCategories(meta, environment, values),
      ])
      topIds.forEach((id) => target.topCategories.set(id, icon))
      subIds.forEach((id) => target.subCategories.set(id, icon))
      break
    }
    case Poi.POIsClassification.CustomPOIs: {
      // Keyed by NAME, not by `selection.id`: the custom-POI features carry
      // the group name in `properties.id` (fetchCustomPOIsLayerSource selects
      // `custom_pois.name as id`), which is also how poisColors keys them.
      target.customGroups.set(selection.name, icon)
      break
    }
  }
}

export function clearPoiLogosCache(): void {
  cache = emptyCache()
  generation.value += 1
}

export function isPoiLogosCacheEmpty(): boolean {
  return (
    cache.brands.size === 0 &&
    cache.tags.size === 0 &&
    cache.locations.size === 0 &&
    cache.topCategories.size === 0 &&
    cache.subCategories.size === 0 &&
    cache.customGroups.size === 0
  )
}

/**
 * Resolves the icon assigned to a POI selection by its name — the key
 * poisColors (and therefore the legend rows) use. Only icons that actually
 * loaded are in the cache, so the legend mirrors what the map draws.
 */
export function getLogoForSelectionName(name: string): PoiLogoIcon | null {
  // Reads the generation ref so reactive callers (the legend's computed
  // rows) re-run when the cache is rebuilt.
  getPoiLogosCacheGeneration()
  return cache.selections.get(name) ?? null
}

/**
 * Resolves the icon for a standard-POI tile feature. Walks the SAME
 * classification order as `getColorFromRamp`
 * (brand > tag > location > top_category > sub_category), so a POI always
 * shows the logo of the selection that drives its color. Custom POIs are
 * NOT resolved here — they live in their own layer drawn on top.
 */
export function getLogoForTilePoi(
  properties: PoiTileProperties | null,
): PoiLogoIcon | null {
  if (!properties) {
    return null
  }
  if (properties.b !== undefined) {
    const icon = cache.brands.get(properties.b)
    if (icon) {
      return icon
    }
  }
  if (properties.cts && cache.tags.size > 0) {
    for (const [tagId, icon] of cache.tags) {
      if (properties.cts.includes(`#${tagId}#`)) {
        return icon
      }
    }
  }
  if (properties.location_name) {
    const icon = cache.locations.get(properties.location_name)
    if (icon) {
      return icon
    }
  }
  if (properties.tc !== undefined) {
    const icon = cache.topCategories.get(properties.tc)
    if (icon) {
      return icon
    }
  }
  if (properties.sc !== undefined && properties.sc !== null) {
    const icon = cache.subCategories.get(properties.sc)
    if (icon) {
      return icon
    }
  }
  return null
}

/**
 * Resolves the icon for a custom-POI feature from its `properties.id`, which
 * holds the group NAME (see indexSelection).
 */
export function getLogoForCustomPoiGroup(
  groupName: string | undefined,
): PoiLogoIcon | null {
  if (!groupName) {
    return null
  }
  return cache.customGroups.get(groupName) ?? null
}
